/**
 * PartnerOnboardingPage — `/integration/partners`.
 *
 * Partner CRUD via service-audit /v1/integration/partners.
 * Lists active partners and allows onboarding new ones with a simple form.
 */
import { type JSX, useCallback, useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';

import { request } from '../../lib/api/client';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { env } from '../../lib/env';

interface Partner {
  id: string;
  name: string;
  contactEmail: string;
  partnerType: string;
  status: string;
  createdAt: string;
}

const PARTNER_TYPES = [
  'EXTERNAL', 'PAYMENT_GATEWAY', 'MDA', 'SMS_PROVIDER', 'LOGISTICS',
] as const;
const DEFAULT_PARTNER_TYPE: typeof PARTNER_TYPES[number] = 'EXTERNAL';

export function PartnerOnboardingPage(): JSX.Element {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [partnerType, setPartnerType] = useState(DEFAULT_PARTNER_TYPE);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request<{ partners: Partner[] }>(
        env.serviceAuditBaseUrl,
        '/v1/integration/partners',
      );
      setPartners(data.partners);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadPartners(); }, [loadPartners]);

  async function handleCreate(): Promise<void> {
    if (!name.trim() || !contactEmail.trim()) return;
    setSaving(true);
    try {
      await request<Partner>(env.serviceAuditBaseUrl, '/v1/integration/partners', {
        method: 'POST',
        body: { name: name.trim(), contactEmail: contactEmail.trim(), partnerType },
      });
      setShowModal(false);
      setName('');
      setContactEmail('');
      setPartnerType(DEFAULT_PARTNER_TYPE);
      await loadPartners();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to onboard partner');
    } finally {
      setSaving(false);
    }
  }

  async function handleSuspend(id: string): Promise<void> {
    if (!window.confirm('Suspend this partner?')) return;
    try {
      await request<unknown>(env.serviceAuditBaseUrl, `/v1/integration/partners/${id}/suspend`, {
        method: 'POST',
      });
      await loadPartners();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to suspend partner');
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Partner onboarding"
        description="Configure and activate new integration partners."
        actions={
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium"
            data-testid="open-partner-modal"
          >
            <Plus className="w-4 h-4" /> New partner
          </button>
        }
      />

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <button type="button" onClick={() => setError(null)} className="ml-3 underline">dismiss</button>
        </div>
      ) : null}

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted text-sm">Loading partners…</div>
        ) : partners.length === 0 ? (
          <div className="p-8 text-center text-muted text-sm" data-testid="no-partners-msg">
            No partners onboarded yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Contact email</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-gray-50" data-testid="partner-row">
                  <td className="p-3 font-medium text-gray-900">{p.name}</td>
                  <td className="p-3 text-xs">{p.partnerType}</td>
                  <td className="p-3 text-xs">{p.contactEmail}</td>
                  <td className="p-3 text-center"><StatusBadge status={p.status} /></td>
                  <td className="p-3 text-right">
                    {p.status === 'ACTIVE' ? (
                      <button
                        type="button"
                        onClick={() => void handleSuspend(p.id)}
                        className="px-2 py-1 text-xs font-medium text-danger rounded hover:bg-red-50"
                        data-testid="suspend-partner-btn"
                      >
                        Suspend
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <p className="text-base font-bold text-gray-900">New partner</p>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted mb-1" htmlFor="partner-name">
                  Partner name
                </label>
                <input
                  id="partner-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Paystack"
                  className="w-full text-sm px-3 py-2 border border-border rounded-lg"
                  data-testid="partner-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted mb-1" htmlFor="partner-email">
                  Contact email
                </label>
                <input
                  id="partner-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="partner@example.com"
                  className="w-full text-sm px-3 py-2 border border-border rounded-lg"
                  data-testid="partner-email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted mb-1" htmlFor="partner-type">
                  Type
                </label>
                <select
                  id="partner-type"
                  value={partnerType}
                  onChange={(e) => setPartnerType(e.target.value as typeof PARTNER_TYPES[number])}
                  className="w-full text-sm px-3 py-2 border border-border rounded-lg"
                >
                  {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleCreate()}
                disabled={saving || !name.trim() || !contactEmail.trim()}
                className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded-lg disabled:opacity-50"
                data-testid="partner-submit-btn"
              >
                {saving ? 'Saving…' : 'Onboard'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
